/**
 * FailurePredictionTimeline Component
 * Shows predicted failure windows with confidence intervals
 * Designed for maintenance planning by technicians and SMEs
 */

import { useMemo } from 'react'
import Plot from 'react-plotly.js'
import {
  AlertTriangle,
  Clock,
  Calendar,
  Wrench,
  Shield,
  CheckCircle,
  Target
} from 'lucide-react'
import clsx from 'clsx'

interface FailurePrediction {
  component: string
  probability: number
  estimated_hours: number
  confidence_interval: {
    lower: number
    upper: number
  }
  severity: 'low' | 'medium' | 'high' | 'critical'
  maintenance_type: 'preventive' | 'corrective' | 'predictive'
  last_maintenance?: string
  recommended_action?: string
}

interface ForecastPoint {
  timestamp: string
  predicted_health: number
  lower_bound: number
  upper_bound: number
}

interface FailurePredictionTimelineProps {
  assetId: string
  predictions: FailurePrediction[]
  forecast?: ForecastPoint[]
  currentHealth: number
  healthThreshold?: number
  className?: string
}

export default function FailurePredictionTimeline({
  assetId,
  predictions,
  forecast,
  currentHealth,
  healthThreshold = 70,
  className
}: FailurePredictionTimelineProps) {

  // Sort predictions by probability (highest first)
  const sortedPredictions = useMemo(() => {
    return [...predictions].sort((a, b) => b.probability - a.probability)
  }, [predictions])

  // Calculate optimal maintenance window
  const optimalWindow = useMemo(() => {
    if (predictions.length === 0) return null

    const criticalPredictions = predictions.filter(p => p.severity === 'critical' || p.severity === 'high')
    if (criticalPredictions.length === 0) return null

    const minHours = Math.min(...criticalPredictions.map(p => p.confidence_interval.lower))
    const maxSafeHours = Math.min(...criticalPredictions.map(p => p.estimated_hours))

    return {
      start: Math.max(minHours * 0.5, 1),
      end: maxSafeHours * 0.8,
      urgency: minHours < 24 ? 'urgent' : minHours < 72 ? 'soon' : 'planned'
    }
  }, [predictions])

  // Generate timeline markers
  const timelineMarkers = useMemo(() => {
    const now = new Date()
    return [
      { hours: 0, label: 'Now', date: now },
      { hours: 24, label: '+24h', date: new Date(now.getTime() + 24 * 60 * 60 * 1000) },
      { hours: 72, label: '+3 days', date: new Date(now.getTime() + 72 * 60 * 60 * 1000) },
      { hours: 168, label: '+1 week', date: new Date(now.getTime() + 168 * 60 * 60 * 1000) },
      { hours: 720, label: '+30 days', date: new Date(now.getTime() + 720 * 60 * 60 * 1000) }
    ]
  }, [])

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return { bg: 'bg-status-critical', text: 'text-status-critical', border: 'border-status-critical' }
      case 'high': return { bg: 'bg-status-warning', text: 'text-status-warning', border: 'border-status-warning' }
      case 'medium': return { bg: 'bg-primary-500', text: 'text-primary-400', border: 'border-primary-500' }
      default: return { bg: 'bg-status-healthy', text: 'text-status-healthy', border: 'border-status-healthy' }
    }
  }

  const getMaintenanceIcon = (type: string) => {
    switch (type) {
      case 'preventive': return <Shield size={14} className="text-status-healthy" />
      case 'corrective': return <Wrench size={14} className="text-status-warning" />
      case 'predictive': return <Target size={14} className="text-primary-400" />
      default: return <Wrench size={14} />
    }
  }

  // Calculate risk level
  const overallRisk = useMemo(() => {
    if (predictions.length === 0) return 'low'
    const maxProbability = Math.max(...predictions.map(p => p.probability))
    const criticalCount = predictions.filter(p => p.severity === 'critical').length

    if (criticalCount > 0 && maxProbability > 0.7) return 'critical'
    if (maxProbability > 0.5) return 'high'
    if (maxProbability > 0.3) return 'medium'
    return 'low'
  }, [predictions])

  return (
    <div className={clsx('rounded-xl border border-surface-700 bg-surface-900 overflow-hidden', className)}>
      {/* Header */}
      <div className={clsx(
        'p-4 border-b border-surface-700',
        overallRisk === 'critical' && 'bg-gradient-to-r from-status-critical/10 to-transparent',
        overallRisk === 'high' && 'bg-gradient-to-r from-status-warning/10 to-transparent',
        overallRisk === 'medium' && 'bg-gradient-to-r from-primary-500/10 to-transparent',
        overallRisk === 'low' && 'bg-gradient-to-r from-status-healthy/10 to-transparent'
      )}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={clsx(
              'p-2 rounded-lg',
              overallRisk === 'critical' && 'bg-status-critical/20 text-status-critical',
              overallRisk === 'high' && 'bg-status-warning/20 text-status-warning',
              overallRisk === 'medium' && 'bg-primary-500/20 text-primary-400',
              overallRisk === 'low' && 'bg-status-healthy/20 text-status-healthy'
            )}>
              {overallRisk === 'critical' ? <AlertTriangle size={20} /> : <Clock size={20} />}
            </div>
            <div>
              <h3 className="font-semibold text-surface-100">Failure Prediction Timeline</h3>
              <p className="text-xs text-surface-400">
                Asset: <span className="font-mono text-primary-400">{assetId}</span>
                <span className="mx-2">|</span>
                {predictions.length} potential issues detected
              </p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-xs text-surface-400">Current Health</p>
            <p className={clsx(
              'text-xl font-bold font-mono',
              currentHealth >= 85 ? 'text-status-healthy' :
              currentHealth >= 70 ? 'text-status-warning' : 'text-status-critical'
            )}>
              {currentHealth.toFixed(0)}%
            </p>
          </div>
        </div>
      </div>

      {/* Optimal Maintenance Window */}
      {optimalWindow && (
        <div className={clsx(
          'p-4 border-b border-surface-700',
          optimalWindow.urgency === 'urgent' && 'bg-status-critical/5',
          optimalWindow.urgency === 'soon' && 'bg-status-warning/5',
          optimalWindow.urgency === 'planned' && 'bg-status-healthy/5'
        )}>
          <div className="flex items-center gap-3">
            <Calendar size={18} className={clsx(
              optimalWindow.urgency === 'urgent' && 'text-status-critical',
              optimalWindow.urgency === 'soon' && 'text-status-warning',
              optimalWindow.urgency === 'planned' && 'text-status-healthy'
            )} />
            <div className="flex-1">
              <p className="text-sm font-medium text-surface-200">
                Optimal Maintenance Window
              </p>
              <p className="text-xs text-surface-400">
                Schedule maintenance between {optimalWindow.start.toFixed(0)}-{optimalWindow.end.toFixed(0)} hours from now
              </p>
            </div>
            <span className={clsx(
              'px-2 py-1 text-xs font-medium rounded',
              optimalWindow.urgency === 'urgent' && 'bg-status-critical/20 text-status-critical',
              optimalWindow.urgency === 'soon' && 'bg-status-warning/20 text-status-warning',
              optimalWindow.urgency === 'planned' && 'bg-status-healthy/20 text-status-healthy'
            )}>
              {optimalWindow.urgency.toUpperCase()}
            </span>
          </div>
        </div>
      )}

      {/* Health Forecast Chart */}
      {forecast && forecast.length > 0 && (
        <div className="p-4 border-b border-surface-700">
          <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3">Health Forecast</h4>
          <Plot
            data={[
              // Confidence interval
              {
                type: 'scatter' as const,
                x: [...forecast.map(f => f.timestamp), ...forecast.slice().reverse().map(f => f.timestamp)],
                y: [...forecast.map(f => f.upper_bound), ...forecast.slice().reverse().map(f => f.lower_bound)],
                fill: 'toself' as const,
                fillcolor: 'rgba(59, 130, 246, 0.1)',
                line: { color: 'transparent' },
                name: 'Confidence Range',
                showlegend: true,
                hoverinfo: 'skip' as const
              },
              // Predicted health
              {
                type: 'scatter' as const,
                mode: 'lines' as const,
                x: forecast.map(f => f.timestamp),
                y: forecast.map(f => f.predicted_health),
                line: { color: '#3b82f6', width: 2 },
                name: 'Predicted Health',
                hovertemplate: '%{y:.1f}%<extra></extra>'
              },
              // Threshold line
              {
                type: 'scatter' as const,
                mode: 'lines' as const,
                x: [forecast[0].timestamp, forecast[forecast.length - 1].timestamp],
                y: [healthThreshold, healthThreshold],
                line: { color: '#ef4444', width: 2, dash: 'dash' },
                name: 'Warning Threshold'
              }
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            ] as any}
            layout={{
              height: 250,
              margin: { t: 20, r: 20, b: 40, l: 50 },
              paper_bgcolor: 'transparent',
              plot_bgcolor: 'transparent',
              font: { color: '#94a3b8', family: 'Inter, sans-serif' },
              xaxis: {
                gridcolor: '#334155',
                tickfont: { size: 10 }
              },
              yaxis: {
                title: { text: 'Health %' },
                range: [0, 100],
                gridcolor: '#334155',
                tickfont: { size: 10 }
              },
              legend: {
                orientation: 'h',
                y: -0.2,
                font: { size: 10 }
              },
              hovermode: 'x unified'
            }}
            config={{ displayModeBar: false, responsive: true }}
            style={{ width: '100%' }}
          />
        </div>
      )}

      {/* Timeline Visual */}
      <div className="p-4 border-b border-surface-700">
        <h4 className="text-xs font-semibold text-surface-400 uppercase mb-4">Failure Risk Timeline</h4>

        {/* Timeline Scale */}
        <div className="relative h-2 bg-surface-800 rounded-full mb-6">
          {timelineMarkers.map((marker) => (
            <div
              key={marker.label}
              className="absolute top-0 -translate-x-1/2"
              style={{ left: `${Math.min((marker.hours / 720) * 100, 100)}%` }}
            >
              <div className="w-0.5 h-4 bg-surface-600" />
              <span className="text-xs text-surface-500 absolute top-5 -translate-x-1/2 whitespace-nowrap">
                {marker.label}
              </span>
            </div>
          ))}

          {/* Prediction markers */}
          {sortedPredictions.slice(0, 5).map((pred) => {
            const colors = getSeverityColor(pred.severity)
            const position = Math.min((pred.estimated_hours / 720) * 100, 98)

            return (
              <div
                key={pred.component}
                className="absolute -top-1"
                style={{ left: `${position}%` }}
              >
                <div
                  className={clsx(
                    'w-4 h-4 rounded-full border-2 -translate-x-1/2',
                    colors.bg,
                    colors.border
                  )}
                  title={`${pred.component}: ${pred.estimated_hours}h`}
                />
              </div>
            )
          })}
        </div>
      </div>

      {/* Predictions List */}
      <div className="p-4">
        <h4 className="text-xs font-semibold text-surface-400 uppercase mb-3">Component Risk Assessment</h4>
        <div className="space-y-3">
          {sortedPredictions.map((pred) => {
            const colors = getSeverityColor(pred.severity)

            return (
              <div
                key={pred.component}
                className={clsx(
                  'p-3 rounded-lg border',
                  `${colors.border}/30`,
                  `${colors.bg.replace('bg-', 'bg-')}/5`
                )}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {getMaintenanceIcon(pred.maintenance_type)}
                    <span className="font-medium text-surface-200 text-sm">
                      {pred.component.replace(/_/g, ' ')}
                    </span>
                    <span className={clsx(
                      'px-1.5 py-0.5 text-xs rounded',
                      `${colors.bg}/20`,
                      colors.text
                    )}>
                      {pred.severity}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className={clsx('font-mono font-bold', colors.text)}>
                      {(pred.probability * 100).toFixed(0)}%
                    </p>
                    <p className="text-xs text-surface-500">failure risk</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 text-xs">
                  <div>
                    <p className="text-surface-400">Est. Time to Failure</p>
                    <p className="font-mono text-surface-200">
                      {pred.estimated_hours}h
                      <span className="text-surface-500 ml-1">
                        ({pred.confidence_interval.lower}-{pred.confidence_interval.upper}h)
                      </span>
                    </p>
                  </div>
                  <div>
                    <p className="text-surface-400">Maintenance Type</p>
                    <p className="text-surface-200 capitalize">{pred.maintenance_type}</p>
                  </div>
                  {pred.last_maintenance && (
                    <div>
                      <p className="text-surface-400">Last Service</p>
                      <p className="text-surface-200">{pred.last_maintenance}</p>
                    </div>
                  )}
                </div>

                {pred.recommended_action && (
                  <div className="mt-2 p-2 bg-surface-900 rounded text-xs">
                    <p className="text-surface-400">Recommended:</p>
                    <p className="text-surface-300">{pred.recommended_action}</p>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>

      {/* Summary */}
      {predictions.length === 0 && (
        <div className="p-6 text-center">
          <CheckCircle size={32} className="text-status-healthy mx-auto mb-2" />
          <p className="text-surface-200 font-medium">No Imminent Failures Predicted</p>
          <p className="text-xs text-surface-400">Asset is operating within normal parameters</p>
        </div>
      )}
    </div>
  )
}
